import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import { config } from '../config';
import './Dashboard.css';

function Dashboard({ tenantId }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    customers: 0,
    cars: 0,
    repairs: 0,
    invoices: 0
  });
  const [repairs, setRepairs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [cars, setCars] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [users, setUsers] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [activeChats, setActiveChats] = useState([]);

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadStats();
      loadRepairs();
      loadInvoices();
      loadCustomers();
      loadPurchases();
      loadInventory();
      loadCars();
      loadAppointments();
      loadFleet();
      loadUsers();
      loadAbsences();
      loadActiveChats();
    }
  }, [tenantId]);
  
  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || '/api'}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const tenantUsers = data.filter(u => u.tenantId === tenantId && u.canHandleAppointments !== false);
        setUsers(tenantUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };
  
  const loadAbsences = async () => {
    try {
      const now = new Date();
      const startDate = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString();
      const endDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
      const data = await api.get(`/absences?startDate=${startDate}&endDate=${endDate}`, tenantId);
      setAbsences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading absences:', error);
    }
  };

  const loadActiveChats = async () => {
    try {
      const data = await api.get('/messages?status=active', tenantId);
      const chats = Array.isArray(data) ? data : [];
      
      const customerChats = {};
      chats.forEach(msg => {
        if (!customerChats[msg.customerId]) {
          customerChats[msg.customerId] = {
            customer: msg.customer,
            messages: [],
            unreadCount: 0,
            lastMessage: null
          };
        }
        customerChats[msg.customerId].messages.push(msg);
        if (!msg.read && msg.fromCustomer) {
          customerChats[msg.customerId].unreadCount++;
        }
        if (!customerChats[msg.customerId].lastMessage || 
            new Date(msg.createdAt) > new Date(customerChats[msg.customerId].lastMessage.createdAt)) {
          customerChats[msg.customerId].lastMessage = msg;
        }
      });
      
      const activeChatsList = Object.values(customerChats)
        .sort((a, b) => {
          if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
          if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
          return new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt);
        })
        .slice(0, 10);
      
      setActiveChats(activeChatsList);
    } catch (error) {
      console.error('Error loading active chats:', error);
    }
  };
  
  const getAvailableUsers = () => {
    const now = new Date();
    return users.filter(user => {
      const isAbsent = absences.some(a => {
        if (a.userId !== user.id) return false;
        const start = new Date(a.startDate);
        const end = new Date(a.endDate);
        return now >= start && now <= end;
      });
      return !isAbsent;
    });
  };
  
  const getAbsentUsers = () => {
    const now = new Date();
    return users.filter(user => {
      return absences.some(a => {
        if (a.userId !== user.id) return false;
        const start = new Date(a.startDate);
        const end = new Date(a.endDate);
        return now >= start && now <= end;
      });
    });
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadStats = async () => {
    try {
      const [customers, cars, repairs, invoices] = await Promise.all([
        api.get('/customers', tenantId),
        api.get('/cars', tenantId),
        api.get('/repairs', tenantId),
        api.get('/invoices', tenantId)
      ]);

      setStats({
        customers: customers.length,
        cars: cars.length,
        repairs: repairs.length,
        invoices: invoices.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await api.get('/invoices', tenantId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadPurchases = async () => {
    try {
      const data = await api.get('/purchases', tenantId);
      setPurchases(data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await api.get('/inventory', tenantId);
      setInventory(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadAppointments = async () => {
    try {
      const data = await api.get('/appointments', tenantId);
      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const loadFleet = async () => {
    try {
      const data = await api.get('/cars?fleetOnly=true', tenantId);
      setFleet(data);
    } catch (error) {
      console.error('Error loading fleet:', error);
    }
  };

  const getReminders = () => {
    return cars
      .filter(car => car.reminderDate)
      .map(car => ({
        ...car,
        reminderDateObj: new Date(car.reminderDate)
      }))
      .sort((a, b) => a.reminderDateObj - b.reminderDateObj)
      .slice(0, 10);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const getReminderStatus = (reminderDate) => {
    const date = new Date(reminderDate);
    const now = new Date();
    if (date < now) {
      return { text: 'Überfällig', color: '#d32f2f' };
    } else if (date <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      return { text: 'Bald fällig', color: '#f57c00' };
    }
    return { text: 'Anstehend', color: '#666' };
  };

  const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const paidRevenue = invoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const pendingRevenue = invoices.filter(i => i.status === 'pending' || i.status === 'sent').reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const overdueRevenue = invoices.filter(i => i.status === 'overdue').reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const overdueInvoicesCount = invoices.filter(i => i.status === 'overdue').length;
  const avgInvoiceAmount = invoices.length > 0 ? totalRevenue / invoices.length : 0;
  
  const openRepairs = repairs.filter(r => r.status === 'pending' || r.status === 'in_progress');
  const openRepairsCount = openRepairs.length;
  const inProgressRepairs = repairs.filter(r => r.status === 'in_progress');
  const inProgressRepairsCount = inProgressRepairs.length;
  const completedRepairs = repairs.filter(r => r.status === 'completed');
  const avgRepairValue = completedRepairs.length > 0 
    ? completedRepairs.reduce((sum, r) => sum + (r.totalAmount || 0), 0) / completedRepairs.length 
    : 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const appointmentsToday = appointments.filter(apt => {
    const aptDate = new Date(apt.dateTime);
    return aptDate >= today && aptDate < tomorrow && apt.status === 'confirmed';
  }).length;

  const appointmentsThisWeek = appointments.filter(apt => {
    const aptDate = new Date(apt.dateTime);
    return aptDate >= today && aptDate < weekEnd && apt.status === 'confirmed';
  }).length;

  const pendingAppointments = appointments.filter(apt => apt.status === 'pending').length;

  const calculateMaterialProfit = () => {
    let totalProfit = 0;
    repairs.forEach(repair => {
      if (repair.materials && Array.isArray(repair.materials)) {
        repair.materials.forEach(material => {
          const inventoryItem = inventory.find(inv => inv.name === material.description);
          if (inventoryItem) {
            const sellingPrice = material.amount || 0;
            const purchasePrice = inventoryItem.purchasePrice || 0;
            const quantity = material.quantity || 1;
            const profit = (sellingPrice - purchasePrice) * quantity;
            totalProfit += profit;
          }
        });
      }
    });
    return totalProfit;
  };

  const materialProfit = calculateMaterialProfit();

  const topCustomer = () => {
    const customerRevenue = {};
    invoices.forEach(invoice => {
      const customerId = invoice.customerId;
      if (customerId) {
        if (!customerRevenue[customerId]) {
          customerRevenue[customerId] = 0;
        }
        customerRevenue[customerId] += invoice.grossAmount || 0;
      }
    });
    
    const top = Object.entries(customerRevenue)
      .sort((a, b) => b[1] - a[1])[0];
    
    if (top) {
      const customer = customers.find(c => c.id === top[0]);
      return customer ? { name: `${customer.firstName} ${customer.lastName}`, revenue: top[1] } : null;
    }
    return null;
  };

  const topCustomerData = topCustomer();

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      
      {/* Stammdaten Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Stammdaten</h2>
        <div className="kpis-grid">
          <div className="kpi-card clickable" onClick={() => navigate('/customers')}>
            <div className="kpi-value">{stats.customers}</div>
            <div className="kpi-label">Kunden</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/cars')}>
            <div className="kpi-value">{stats.cars}</div>
            <div className="kpi-label">Autos</div>
          </div>
          {topCustomerData && (
            <div className="kpi-card clickable" onClick={() => {
              const customer = customers.find(c => `${c.firstName} ${c.lastName}` === topCustomerData.name);
              if (customer) navigate(`/customers/${customer.id}`);
            }}>
              <div className="kpi-value">{topCustomerData.name.length > 20 ? topCustomerData.name.substring(0, 20) + '...' : topCustomerData.name}</div>
              <div className="kpi-label">Top Kunde ({topCustomerData.revenue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency})</div>
            </div>
          )}
        </div>
      </section>

      {/* Reparaturen Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Reparaturen</h2>
        <div className="kpis-grid">
          <div className="kpi-card clickable" onClick={() => navigate('/repairs')}>
            <div className="kpi-value">{stats.repairs}</div>
            <div className="kpi-label">Reparaturen</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/repairs')}>
            <div className="kpi-value">{openRepairsCount}</div>
            <div className="kpi-label">Offene Reparaturen</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/repairs')}>
            <div className="kpi-value">{inProgressRepairsCount}</div>
            <div className="kpi-label">In Bearbeitung</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/repairs')}>
            <div className="kpi-value">{avgRepairValue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Ø Reparaturwert</div>
          </div>
        </div>
      </section>

      {/* Rechnungen Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Rechnungen & Finanzen</h2>
        <div className="kpis-grid">
          <div className="kpi-card clickable" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{stats.invoices}</div>
            <div className="kpi-label">Rechnungen</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{totalRevenue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Gesamtumsatz</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{paidRevenue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Bezahlt</div>
          </div>
          <div className="kpi-card clickable revenue warning" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{pendingRevenue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Ausstehend</div>
          </div>
          <div className="kpi-card clickable revenue danger" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{overdueRevenue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Überfällig</div>
          </div>
          <div className="kpi-card clickable danger" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{overdueInvoicesCount}</div>
            <div className="kpi-label">Überfällige Rechnungen</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{avgInvoiceAmount.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Ø Rechnungsbetrag</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/invoices')}>
            <div className="kpi-value">{materialProfit.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Gewinn aus Material</div>
          </div>
        </div>
      </section>

      {/* Fahrzeuge Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Fahrzeuge</h2>
        <div className="kpis-grid">
          <div className="kpi-card clickable" onClick={() => navigate('/fleet/inventory')}>
            <div className="kpi-value">{fleet.filter(v => !v.status || v.status === 'owned').length}</div>
            <div className="kpi-label">Eigene Fahrzeuge</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/fleet/inventory')}>
            <div className="kpi-value">{fleet.filter(v => !v.status || v.status === 'owned').reduce((sum, v) => sum + (v.purchasePrice || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Gesamter Ankaufswert</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/fleet/inventory')}>
            <div className="kpi-value">{fleet.filter(v => !v.status || v.status === 'owned').reduce((sum, v) => sum + (v.marketValue || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Gesamter Marktwert</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/fleet/sales')}>
            <div className="kpi-value">{fleet.filter(v => v.status === 'sold').length}</div>
            <div className="kpi-label">Verkaufte Fahrzeuge</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/fleet/sales')}>
            <div className="kpi-value">{fleet.filter(v => v.status === 'sold').reduce((sum, v) => sum + (v.salePrice || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currency}</div>
            <div className="kpi-label">Gesamter Verkaufswert</div>
          </div>
          <div className="kpi-card clickable revenue" onClick={() => navigate('/fleet/sales')}>
            <div className="kpi-value">
              {(() => {
                const sold = fleet.filter(v => v.status === 'sold');
                if (sold.length === 0) return '0';
                const profit = sold.reduce((sum, v) => {
                  const salePrice = v.salePrice || 0;
                  const purchasePrice = v.purchasePrice || 0;
                  return sum + (salePrice - purchasePrice);
                }, 0);
                return `${profit >= 0 ? '+' : ''}${profit.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
              })()} {currency}
            </div>
            <div className="kpi-label">Gewinn/Verlust (Verkäufe)</div>
          </div>
        </div>
      </section>

      {/* Termine Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Termine</h2>
        <div className="kpis-grid">
          <div className="kpi-card clickable" onClick={() => navigate('/appointments')}>
            <div className="kpi-value">{appointmentsToday}</div>
            <div className="kpi-label">Termine heute</div>
          </div>
          <div className="kpi-card clickable" onClick={() => navigate('/appointments')}>
            <div className="kpi-value">{appointmentsThisWeek}</div>
            <div className="kpi-label">Termine diese Woche</div>
          </div>
          <div className="kpi-card clickable warning" onClick={() => navigate('/appointments')}>
            <div className="kpi-value">{pendingAppointments}</div>
            <div className="kpi-label">Ausstehende Termine</div>
          </div>
        </div>
      </section>

      {/* Mitarbeiter Verfügbarkeit Section */}
      <section className="dashboard-section">
        <h2 className="section-title">Mitarbeiter Verfügbarkeit</h2>
        <div className="team-availability-list">
          {users.map(user => {
            const now = new Date();
            const isAbsent = absences.some(a => {
              if (a.userId !== user.id) return false;
              const start = new Date(a.startDate);
              const end = new Date(a.endDate);
              return now >= start && now <= end;
            });
            const absence = absences.find(a => {
              if (a.userId !== user.id) return false;
              const start = new Date(a.startDate);
              const end = new Date(a.endDate);
              return now >= start && now <= end;
            });
            
            return (
              <div 
                key={user.id} 
                className="team-availability-item"
                onClick={() => navigate(`/account/${user.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="team-availability-avatar">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.username} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      borderRadius: '50%', 
                      backgroundColor: isAbsent ? '#f44336' : '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2em'
                    }}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div 
                    className="team-availability-status"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: '2px solid white',
                      backgroundColor: isAbsent ? '#f44336' : '#4caf50'
                    }}
                  />
                </div>
                <div className="team-availability-info">
                  <div className="team-availability-name">{user.username}</div>
                  {isAbsent && absence && (
                    <div className="team-availability-status-text" style={{ fontSize: '0.85em', color: '#666' }}>
                      Abwesend bis {formatDate(absence.endDate)}
                    </div>
                  )}
                  {!isAbsent && (
                    <div className="team-availability-status-text" style={{ fontSize: '0.85em', color: '#4caf50' }}>
                      Verfügbar
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              Keine Mitarbeiter gefunden
            </div>
          )}
        </div>
      </section>

      {/* Aktive Chat-Anfragen Section */}
      {activeChats.length > 0 && (
        <section className="dashboard-section">
          <h2 className="section-title">Aktive Chat-Anfragen</h2>
          <div className="active-chats-list">
            {activeChats.map((chat, index) => {
              const customer = chat.customer;
              if (!customer) return null;
              
              const lastMessage = chat.lastMessage;
              const timeAgo = lastMessage ? (() => {
                const now = new Date();
                const msgDate = new Date(lastMessage.createdAt);
                const diffMs = now - msgDate;
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);
                
                if (diffMins < 1) return 'gerade eben';
                if (diffMins < 60) return `vor ${diffMins} Min`;
                if (diffHours < 24) return `vor ${diffHours} Std`;
                if (diffDays === 1) return 'gestern';
                return `vor ${diffDays} Tagen`;
              })() : '';
              
              return (
                <div 
                  key={customer.id || index}
                  className="active-chat-item"
                  onClick={() => navigate(`/customers/${customer.id}?tab=chat`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="active-chat-header">
                    <div className="active-chat-customer">
                      <strong>{customer.firstName} {customer.lastName}</strong>
                      {chat.unreadCount > 0 && (
                        <span className="unread-badge">{chat.unreadCount}</span>
                      )}
                    </div>
                    <span className="active-chat-time">{timeAgo}</span>
                  </div>
                  {lastMessage && (
                    <div className="active-chat-preview">
                      {lastMessage.fromCustomer ? 'Kunde: ' : 'Werkstatt: '}
                      {lastMessage.message.length > 60 
                        ? lastMessage.message.substring(0, 60) + '...' 
                        : lastMessage.message}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Erinnerungen Section */}
      {getReminders().length > 0 && (
        <section className="dashboard-section">
          <h2 className="section-title">Erinnerungen</h2>
          <div className="reminders-section">
          <div className="reminders-list">
            {getReminders().map(car => {
              const status = getReminderStatus(car.reminderDate);
              const customer = customers.find(c => c.id === car.customerId);
              return (
                <div 
                  key={car.id} 
                  className="reminder-item"
                  onClick={() => navigate(`/cars/${car.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="reminder-date" style={{ color: status.color }}>
                    {formatDate(car.reminderDate)}
                  </div>
                  <div className="reminder-content">
                    <div className="reminder-car">
                      <strong>{car.make} {car.model}</strong>
                      {customer && (
                        <span style={{ fontSize: '0.9em', color: '#666', marginLeft: '0.5rem' }}>
                          ({customer.firstName} {customer.lastName})
                        </span>
                      )}
                    </div>
                    {car.reminderNote && (
                      <div className="reminder-note">{car.reminderNote}</div>
                    )}
                  </div>
                  <div className="reminder-status" style={{ color: status.color }}>
                    {status.text}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Dashboard;
